import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquarePlus, Pencil, Plus } from "lucide-react";
import {
  type LibraryItem,
  expandSpintax,
  useCreateLibraryItem,
  useLibrary,
} from "@/lib/library";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ContentLibrary } from "./ContentLibrary";
import { KIND_ICONS } from "./libraryKinds";
import { legacySnippets, settleLegacySnippets } from "./replySnippets";

/**
 * Reply templates — the materials of the content library marked "quick", one
 * click away from the reply field. A text goes into the field (one variant of
 * its spintax), a file becomes the reply's attachment; nothing is sent by the
 * click. Templates are the team's: what one operator adds here, all see.
 *
 * They used to be each browser's own. What this browser still holds is offered
 * to be moved to the library, once.
 */
export function SnippetPicker({
  onPick,
  onPickMaterial,
  currentText,
}: {
  onPick: (text: string) => void;
  /** A quick material that is a file was chosen. */
  onPickMaterial: (item: LibraryItem) => void;
  /** Current composer draft — offered as "save as template" when non-empty. */
  currentText?: string;
}) {
  const { t } = useTranslation();
  const library = useLibrary();
  const create = useCreateLibraryItem();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  // The material whose form is open in the library ("Edit" on a row).
  const [editId, setEditId] = useState<string | null>(null);
  const [legacy, setLegacy] = useState(legacySnippets);
  const [moving, setMoving] = useState(false);

  const quick = (library.data?.items ?? []).filter((item) => item.is_quick);
  const isTemplate = (text: string) =>
    quick.some((item) => item.kind === "text" && item.text === text);

  const add = (text: string) => {
    const trimmed = text.trim();
    if (trimmed && !isTemplate(trimmed)) create.mutate({ text: trimmed, is_quick: true });
  };
  const submitNew = (e: FormEvent) => {
    e.preventDefault();
    add(draft);
    setDraft("");
  };
  const moveLegacy = async () => {
    setMoving(true);
    try {
      for (const snippet of legacy) {
        const text = snippet.text.trim();
        if (!isTemplate(text)) await create.mutateAsync({ text, is_quick: true });
      }
      settleLegacySnippets();
      setLegacy([]);
    } catch {
      /* reported by the app's error toast; the offer stays for another try */
    } finally {
      setMoving(false);
    }
  };

  const canSaveCurrent = !!currentText && currentText.trim().length > 0;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          type="button"
          aria-label={t("inbox.snippets")}
          title={t("inbox.snippets") ?? ""}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <MessageSquarePlus size={18} strokeWidth={2} aria-hidden="true" />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-2">
          <div className="mb-2 flex items-center justify-between px-1">
            <p className="text-xs font-medium text-fg-muted">{t("inbox.snippets")}</p>
            {canSaveCurrent && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-1.5 text-xs"
                onClick={() => add(currentText!)}
              >
                {t("inbox.snippet_save_current")}
              </Button>
            )}
          </div>

          {legacy.length > 0 && library.data && (
            <div className="mb-2 space-y-1.5 rounded-md border border-accent/30 bg-accent/10 p-2 text-xs">
              <p>{t("inbox.snippets_legacy", { count: legacy.length })}</p>
              <div className="flex gap-1.5">
                <Button size="sm" className="h-7 px-2 text-xs" loading={moving} onClick={moveLegacy}>
                  {t("inbox.snippets_legacy_move")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  disabled={moving}
                  onClick={() => {
                    settleLegacySnippets();
                    setLegacy([]);
                  }}
                >
                  {t("inbox.snippets_legacy_skip")}
                </Button>
              </div>
            </div>
          )}

          <ul className="max-h-56 space-y-1 overflow-y-auto">
            {quick.length === 0 ? (
              <li className="px-2 py-3 text-center text-xs text-fg-muted">
                {t(library.isLoading ? "inbox.loading" : "inbox.snippets_empty")}
              </li>
            ) : (
              quick.map((item) => {
                const Icon = KIND_ICONS[item.kind];
                const label = item.kind === "text" ? (item.text ?? "") : item.title;
                return (
                  <li key={item.id} className="flex items-stretch gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (item.kind === "text") onPick(expandSpintax(item.text ?? ""));
                        else onPickMaterial(item);
                        setOpen(false);
                      }}
                      className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      title={label}
                    >
                      {item.kind !== "text" && (
                        <Icon
                          size={14}
                          strokeWidth={2}
                          aria-hidden="true"
                          className="shrink-0 text-fg-muted"
                        />
                      )}
                      <span className="truncate">{label}</span>
                    </button>
                    <button
                      type="button"
                      aria-label={t("inbox.snippet_edit")}
                      title={t("inbox.snippet_edit") ?? ""}
                      onClick={() => {
                        setOpen(false);
                        setEditId(item.id);
                      }}
                      className="flex w-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-secondary hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          <form
            onSubmit={submitNew}
            className="mt-2 flex items-center gap-1 border-t border-border pt-2"
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={2000}
              placeholder={t("inbox.snippet_add_placeholder") ?? ""}
              className="min-w-0 flex-1 rounded-md border border-border bg-bg-primary px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
            <Button
              type="submit"
              size="icon"
              variant="secondary"
              className="h-8 w-8 shrink-0"
              disabled={!draft.trim()}
              aria-label={t("inbox.snippet_add")}
              title={t("inbox.snippet_add") ?? ""}
            >
              <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
            </Button>
          </form>
        </PopoverContent>
      </Popover>
      <ContentLibrary
        open={editId !== null}
        onOpenChange={(next) => !next && setEditId(null)}
        editId={editId}
      />
    </>
  );
}
SnippetPicker.displayName = "SnippetPicker";
